import "./index.scss";

import { Box, MenuItem, Modal, Stack } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import DatePicker from "components/DatePicker";
import Dropdown from "components/Dropdown";
import Input from "components/Input";
import PhoneInput from "components/PhoneInput";
import ScreenLoader from "components/ScreenLoader";
import TitleWithOptions from "components/TitleWithOptions";
import {
  COUNTRIES,
  COUNTRY_ISO3_CODES,
  DATE_FORMAT,
  DATE_FORMAT_STANDARD,
  MYSQL_DATE_FORMAT,
  MYSQL_DATE_FORMAT_PADDED,
  STAFF_STATUS_OPTIONS,
  STAFF_TYPES,
  USA_STATES,
} from "constants/index";
import dayjs from "dayjs";
import { bufferFitlinxxId, isOver100YearsOld, validatePhoneNumber } from "helpers/utils";
import _ from "lodash";
import {
  getLkpRandomFitlinxxId,
  verifyFitlinxxIdAvailability,
} from "modules/lookup/lookupServices";
import { createStaff, updateStaff } from "modules/staffs/staffServices";
import moment from "moment";
import { phone } from "phone";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { validateEmail } from "../../modules/api/emailValidation";

const StaffsAddEditComp = ({ open, onClose, data, callback = null }) => {
  const {
    handleSubmit,
    control,
    watch,
    register,
    formState: { errors },
    getValues,
    setValue,
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingRandomFitlinxId, setLoadingRandomFitlinxId] = useState(false);
  const [loadingFitlinxIdVerification, setLoadingFitlinxIdVerification] = useState(false);
  const [showDataWillBeLostModal, setShowDataWillBeLostModal] = useState(false);

  useEffect(() => {
    setValue("first_name", data?.first_name || "");
    setValue("last_name", data?.last_name || "");
    setValue("birth_date", data?.birth_date ? dayjs(data?.birth_date).format("YYYY-MM-DD") : "");
    setValue("gender", data?.gender || "");
    setValue("weight", data?.weight || "");
    setValue("title", data?.title || "");
    setValue("fitlinxx_id", data?.fitlinxx_id ? bufferFitlinxxId(data?.fitlinxx_id) : "");
    setValue("type", data?.type_string || "");
    setValue("status", data?.status_string || "");

    setValue("email", data?.email || "");
    setValue("phone", data?.phone || "");
    setValue("other_phone", data?.other_phone || "");
    setValue("address", data?.address || "");
    setValue("address_2", data?.address_2 || "");
    setValue("city", data?.city || "");
    setValue("state", data?.state || "");
    setValue("postal_code", data?.postal_code || "");
    setValue("country", data?.country || "");

    if (!data) {
      handleAutoSelectId();
    }
  }, [data]);

  const watchCountry = watch("country");

  const handleAutoSelectId = () => {
    setLoadingRandomFitlinxId(true);
    getLkpRandomFitlinxxId().then((res) => {
      setLoadingRandomFitlinxId(false);
      if (res.success) {
        setValue("fitlinxx_id", bufferFitlinxxId(res.data));
      }
    });
  };

  const handleCheckId = () => {
    const fitlinxxId = getValues("fitlinxx_id");
    if (fitlinxxId) {
      setLoadingFitlinxIdVerification(true);
      verifyFitlinxxIdAvailability({ fitlinxx_id: fitlinxxId })
        .then((res) => {
          if (res.success) {
            toast(`The chosen FitLinxx ID is available.`, { type: "success" });
            setValue("fitlinxx_id", bufferFitlinxxId(fitlinxxId));
          }
        })
        .catch((e) => {
          toast(e[0], { type: "error" });
          setValue("fitlinxx_id", "");
        })
        .finally(() => setLoadingFitlinxIdVerification(false));
    }
  };

  const formatZipCode = (e) => {
    const postCode = e.target.value;
    if (postCode?.length && postCode?.length < 5 && watchCountry === "USA") {
      setValue("postal_code", `${"0".repeat(5 - postCode?.length)}${postCode}`);
    }
  };

  const handleSubmitForm = async (fData) => {
    setIsLoading(true);
    if (!!data && bufferFitlinxxId(data?.fitlinxx_id) === bufferFitlinxxId(fData?.fitlinxx_id)) {
    } else {
      try {
        const res = await verifyFitlinxxIdAvailability({ fitlinxx_id: fData?.fitlinxx_id });
        fData.fitlinxx_id = bufferFitlinxxId(fData?.fitlinxx_id);
      } catch (e) {
        console.log(`Toasting now. ${JSON.stringify(e)}`);
        toast(e[0], { type: "error" });
        setIsLoading(false);
        return;
      }
    }
    let result = null;
    if (data) {
      result = await updateStaff(data?.id, fData);
    } else {
      result = await createStaff(fData);
    }
    setIsLoading(false);
    if (result.success) {
      if (!!data) toast("Staff edited successfully", { type: "success" });
      else toast("Staff added successfully", { type: "success" });
      if (callback) callback();
      onClose();
    } else {
      toast(result.message, { type: "error" });
    }
  };

  const onCloseConfirm = (discardChanges = false) => {
    if (discardChanges) {
      setShowDataWillBeLostModal(false);
      onClose();
    } else {
      setShowDataWillBeLostModal(false);
    }
  };

  const handleCheckEmailIsValid = async (value): Promise<boolean> => {
    const email = value.toLowerCase();
    return await validateEmail(email);
  };

  return (
    <>
      <Modal open={open} onClose={() => setShowDataWillBeLostModal(true)}>
        <Stack className="personal-details-modal-container">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="40px"
            className="header"
          >
            <h1>{data ? `Edit` : `Add`} Staff</h1>
            <DeleteIcon
              width="24px"
              height="24px"
              onClick={() => setShowDataWillBeLostModal(true)}
            />
          </Stack>
          {isLoading && <ScreenLoader isScreenLoader />}
          <Box className={isLoading && `isHidden`}>
            <Box className="memberForm">
              <form onSubmit={handleSubmit(handleSubmitForm)}>
                {isLoading && <ScreenLoader isScreenLoader />}
                {/* {!isLoading && ( */}
                <Stack height={"100%"}>
                  <div>
                    <TitleWithOptions title="Personal Info" />
                    <Stack direction="row" gap={4} my={4}>
                      <Stack flex={1} gap={2}>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            First name<span>*</span>
                          </Box>
                          <Controller
                            name="first_name"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input
                                  value={value}
                                  onChange={onChange}
                                  hasError={!_.isEmpty(errors.first_name)}
                                  maxLength={20}
                                />
                                {errors.first_name?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Last name<span>*</span>
                          </Box>
                          <Controller
                            name="last_name"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input
                                  value={value}
                                  onChange={onChange}
                                  hasError={!_.isEmpty(errors.last_name)}
                                  maxLength={30}
                                />
                                {errors.last_name?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Date of Birth<span>*</span>
                          </Box>
                          <Controller
                            name="birth_date"
                            control={control}
                            rules={{
                              required: true,
                              validate: (value: Date) => {
                                if (
                                  getValues("birth_date") === "Invalid date" ||
                                  isOver100YearsOld(getValues("birth_date"))
                                ) {
                                  return false;
                                } else {
                                  return true;
                                }
                              },
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <DatePicker
                                  className="base-dropdown"
                                  fullWidth={true}
                                  value={
                                    new Date(
                                      moment(value, MYSQL_DATE_FORMAT).format(DATE_FORMAT_STANDARD)
                                    )
                                  }
                                  onChange={(v) => {
                                    v.setHours(0);
                                    setValue(
                                      "birth_date",
                                      moment(v).format(MYSQL_DATE_FORMAT_PADDED)
                                    );
                                  }}
                                  maxDate={
                                    new Date(
                                      moment().subtract(10, "year").format(DATE_FORMAT_STANDARD)
                                    )
                                  }
                                  minDate={
                                    new Date(
                                      moment().subtract(130, "year").format(DATE_FORMAT_STANDARD)
                                    )
                                  }
                                />
                                {errors.birth_date?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                                {errors.birth_date?.type === "validate" && (
                                  <h4 className="error">Please enter a valid date</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Gender<span>*</span>
                          </Box>
                          <Controller
                            name="gender"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Dropdown
                                  fullWidth
                                  value={value}
                                  onChange={(e) => onChange(e.target.value)}
                                >
                                  <MenuItem value="">Gender</MenuItem>
                                  <MenuItem value="M">Male</MenuItem>
                                  <MenuItem value="F">Female</MenuItem>
                                </Dropdown>
                                {errors.gender?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Weight
                          </Box>
                          <Controller
                            name="weight"
                            control={control}
                            rules={{ max: 9999, required: false }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input
                                  value={value}
                                  onChange={onChange}
                                  type="number"
                                  min="1"
                                  hasError={!_.isEmpty(errors.weight)}
                                />
                                {errors.weight?.type === "max" && (
                                  <h4 className="error">Weight can not be more than 9999</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>

                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            FitLinxx ID<span>*</span>
                          </Box>
                          <Stack direction={"row"} flexGrow={1} gap={`10px`}>
                            <Controller
                              name="fitlinxx_id"
                              control={control}
                              rules={{ required: true }}
                              render={({ field: { onChange, value, ref } }) => (
                                <Stack spacing={1} flexGrow={1}>
                                  <Input
                                    value={value}
                                    onChange={onChange}
                                    onBlur={(e) => onChange(bufferFitlinxxId(e.target.value))}
                                    hasError={!_.isEmpty(errors.fitlinxx_id)}
                                    maxLength={5}
                                    minLength={5}
                                    extraComp={
                                      <Button
                                        label={"Auto Select ID"}
                                        className={
                                          "text-button autoSelectIdButton buttonDisabledLoadingCursor"
                                        }
                                        onClick={handleAutoSelectId}
                                        type="button"
                                        disabled={loadingRandomFitlinxId}
                                      />
                                    }
                                  />
                                  {errors.fitlinxx_id?.type === "required" && (
                                    <h4 className="error">This field is required</h4>
                                  )}
                                </Stack>
                              )}
                            />
                            <Button
                              label={"Check ID"}
                              color={"primary-blue"}
                              onClick={handleCheckId}
                              type="button"
                              className="buttonDisabledLoadingCursor"
                              disabled={loadingFitlinxIdVerification}
                            />
                          </Stack>
                        </Stack>

                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Staff Type<span>*</span>
                          </Box>
                          <Controller
                            name="type"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Dropdown
                                  fullWidth
                                  value={value}
                                  onChange={(e) => onChange(e.target.value)}
                                >
                                  {STAFF_TYPES.filter((o) => !!o.value).map((t) => (
                                    <MenuItem value={t.value}>{t.label}</MenuItem>
                                  ))}
                                </Dropdown>
                                {errors.type?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>

                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Staff Status<span>*</span>
                          </Box>
                          <Controller
                            name="status"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Dropdown
                                  fullWidth
                                  value={value}
                                  onChange={(e) => onChange(e.target.value)}
                                >
                                  {STAFF_STATUS_OPTIONS.filter((o) => !!o.value).map((t) => (
                                    <MenuItem
                                      disabled={!!data?.assigned_members && t.value === "IN_ACTIVE"}
                                      value={t.value}
                                    >
                                      {t.label}
                                    </MenuItem>
                                  ))}
                                </Dropdown>
                                {errors.status?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>

                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Title
                          </Box>
                          <Controller
                            name="title"
                            control={control}
                            // rules={{ required: true }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input
                                  value={value}
                                  onChange={onChange}
                                  hasError={!_.isEmpty(errors.title)}
                                  maxLength={20}
                                />
                                {errors.title?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                      </Stack>

                      <Stack flex={1} gap={2}>
                        {/* <Stack direction={"row"} alignContent="center" gap="40px">
                      <Box className="fieldLabel" pt={"14px"}>
                        Online Username
                      </Box>
                      <Controller
                        name="username"
                        control={control}
                        // rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <Input
                              value={value}
                              onChange={onChange}
                              hasError={!_.isEmpty(errors.username)}
                            />
                            {errors.username?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Stack> */}
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Email address<span>*</span>
                          </Box>
                          <Controller
                            name="email"
                            control={control}
                            rules={{
                              pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                              required: true,
                              validate: (value) => handleCheckEmailIsValid(value),
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input
                                  value={value}
                                  type="email"
                                  onChange={onChange}
                                  hasError={!_.isEmpty(errors.email)}
                                  maxLength={50}
                                />
                                {errors.email?.type === "required" && (
                                  <h4 className="error">This field is required</h4>
                                )}
                                {errors.email?.type === "pattern" && (
                                  <h4 className="error">Invalid email</h4>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Primary phone
                          </Box>
                          <Controller
                            name="phone"
                            control={control}
                            rules={{
                              maxLength: 15,
                              required: false,
                              validate: (value: string) => {
                                return validatePhoneNumber(value, watchCountry);
                              },
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <PhoneInput
                                  iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                                  international={true}
                                  withCountryCallingCode={true}
                                  value={value}
                                  onChange={onChange}
                                  hasError={!_.isEmpty(errors.phone)}
                                />
                                {errors.phone?.type === "validate" && (
                                  <>
                                    <h4 className="error">Please add a valid phone number.</h4>
                                    <p className="phone-hint">
                                      {COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}
                                    </p>
                                  </>
                                )}
                                {errors.phone?.type === "maxLength" && (
                                  <>
                                    <h4 className="error">Length can not be greater than 15.</h4>
                                  </>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Other phone
                          </Box>
                          <Controller
                            name="other_phone"
                            control={control}
                            rules={{
                              maxLength: 20,
                              required: false,
                              validate: (value: string) => {
                                return validatePhoneNumber(value, watchCountry);
                              },
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <PhoneInput
                                  iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                                  international={true}
                                  withCountryCallingCode={true}
                                  value={value}
                                  onChange={onChange}
                                  hasError={!_.isEmpty(errors.other_phone)}
                                />
                                {errors.other_phone?.type === "validate" && (
                                  <>
                                    <h4 className="error">Please add a valid phone number.</h4>
                                    <p className="phone-hint">
                                      {COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}
                                    </p>
                                  </>
                                )}
                                {errors.other_phone?.type === "maxLength" && (
                                  <>
                                    <h4 className="error">Length can not be greater than 20.</h4>
                                  </>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Address
                          </Box>
                          <Controller
                            name="address"
                            control={control}
                            rules={{ required: false }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input value={value} onChange={onChange} maxLength={50} />
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}></Box>
                          <Controller
                            name="address_2"
                            control={control}
                            rules={{ required: false }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input value={value} onChange={onChange} maxLength={50} />
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            City
                          </Box>
                          <Controller
                            name="city"
                            control={control}
                            rules={{ required: false }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input value={value} onChange={onChange} maxLength={30} />
                              </Stack>
                            )}
                          />
                        </Stack>
                        {watchCountry === "USA" && (
                          <Stack direction={"row"} alignContent="center" gap="40px">
                            <Box className="fieldLabel" pt={"14px"}>
                              State
                            </Box>
                            <Controller
                              name="state"
                              control={control}
                              rules={{ required: false }}
                              render={({ field: { onChange, value, ref } }) => (
                                <Stack spacing={1} flexGrow={1}>
                                  <Dropdown
                                    fullWidth
                                    value={value}
                                    onChange={(e) => setValue("state", e.target.value)}
                                  >
                                    <MenuItem value="">State</MenuItem>
                                    {USA_STATES.map((m, index) => (
                                      <MenuItem value={m} key={`state_${index}`}>
                                        {m}
                                      </MenuItem>
                                    ))}
                                  </Dropdown>
                                </Stack>
                              )}
                            />
                          </Stack>
                        )}

                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Zip Code
                          </Box>
                          <Controller
                            name="postal_code"
                            control={control}
                            rules={{
                              required: false,
                              validate: (value: string) => {
                                if (value && watchCountry === "USA") {
                                  return value.length === 5;
                                } else {
                                  return true;
                                }
                              },
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Input
                                  value={value}
                                  onChange={onChange}
                                  onBlur={formatZipCode}
                                  maxLength={9}
                                />
                                {errors.postal_code?.type === "validate" && (
                                  <>
                                    <h4 className="error">Length must be 5</h4>
                                  </>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                        <Stack direction={"row"} alignContent="center" gap="40px">
                          <Box className="fieldLabel" pt={"14px"}>
                            Country
                          </Box>
                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: false }}
                            defaultValue="USA"
                            render={({ field: { onChange, value, ref } }) => (
                              <Stack spacing={1} flexGrow={1}>
                                <Dropdown
                                  fullWidth
                                  value={value}
                                  onChange={(e) => setValue("country", e.target.value)}
                                >
                                  <MenuItem value="">Country</MenuItem>
                                  {COUNTRIES.map((m, index) => (
                                    <MenuItem value={m} key={`country_${index}`}>
                                      {m}
                                    </MenuItem>
                                  ))}
                                </Dropdown>
                              </Stack>
                            )}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </div>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  spacing={3}
                  mt={4}
                >
                  <Button
                    type="button"
                    label="Cancel"
                    className="text-button"
                    onClick={() => setShowDataWillBeLostModal(true)}
                  />
                  <Button label="Save" className="confirm-button" type="submit" />
                </Stack>
              </form>
            </Box>
          </Box>
        </Stack>
      </Modal>

      <ConfirmationBox
        open={showDataWillBeLostModal}
        onClose={onCloseConfirm}
        title={"Discard Changes?"}
        message={<>If answered Yes, all information filled will be lost.</>}
        type={"confirmation"}
      />
    </>
  );
};

export default StaffsAddEditComp;
